import { useParams, useSearchParams } from 'react-router-dom';

import type { DtoV1BoltAuditSectionedEntityAudit } from '~/__generated__/api';
import AccordionContainer from '~/common/components/AccordionContainer';
import Stack from '~/common/components/Stacks/Stack';

import AuditTable from './AuditTable';
import Layout from './Layout';
import { useGetMortgageHistoryAuditData } from './hooks';

interface MortgageHistoryAuditField {
  fieldLabel?: string;
  fieldValue?: string[];
  userResponded?: string;
  dateTimestamp?: string;
  changeHistory?: Array<{
    value: string;
    user: string;
    date: string;
  }>;
  [key: string]: any;
}

interface MortgageHistoryAuditSection {
  type?: string;
  fields?: MortgageHistoryAuditField[];
}

const PAYMENT_MONTH_REGEX = /^(\d{4})-(\d{2})$/;
const LOAN_ENTITY_TYPE = 0;

const monthNames: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

function isPaymentMonth(fieldKey?: string): boolean {
  if (!fieldKey) return false;
  const match = PAYMENT_MONTH_REGEX.exec(fieldKey);
  if (!match) return false;

  const month = Number.parseInt(match[2], 10);
  return month >= 1 && month <= 12;
}

function formatPaymentMonth(fieldKey: string): string {
  const match = PAYMENT_MONTH_REGEX.exec(fieldKey);
  if (!match) return fieldKey;

  const year = match[1];
  const month = Number.parseInt(match[2], 10);
  const monthName = monthNames[month] || `Month ${month}`;

  return `${monthName} ${year}`;
}

function groupFields(sections: MortgageHistoryAuditSection[]): {
  questions: any[];
  payments: any[];
} {
  const questions: any[] = [];
  const payments: any[] = [];

  if (!sections) return { questions, payments };

  sections.forEach((section) => {
    section.fields?.forEach((field: any) => {
      const key = field.key || '';
      if (isPaymentMonth(key)) {
        payments.push({
          // display the formatted month in the table key
          key: formatPaymentMonth(key),
          originalKey: key,
          value: field.value,
        });
      } else {
        questions.push({ key: field.key, value: field.value });
      }
    });
  });

  // Sort payments by original year-month key in descending order
  payments.sort((a, b) =>
    (b.originalKey || '').localeCompare(a.originalKey || ''),
  );

  return { questions, payments };
}

function renderLiabilitySection(
  entity: DtoV1BoltAuditSectionedEntityAudit,
): React.ReactNode {
  const sections =
    (entity.auditSections as MortgageHistoryAuditSection[]) || [];
  const { questions, payments } = groupFields(sections);

  const paymentHeader =
    payments.length >= 24 ? '24 Months of Payment' : '12 Months of Payments';

  return (
    <div>
      <Stack gap={8}>
        {questions.length > 0 && (
          <div>
            <AuditTable
              data={questions}
              section={{}}
              headers={{ loanAction: 'Loan Action' }}
            />
          </div>
        )}

        {payments.length > 0 && (
          <div>
            <AuditTable
              data={payments}
              section={{}}
              headers={{ loanAction: paymentHeader }}
            />
          </div>
        )}
      </Stack>
    </div>
  );
}

export default function MortgageHistoryAuditHistory() {
  const { pageEnum, entityEnum } = useParams();
  const [searchParams, _] = useSearchParams();

  const references: Record<string, string> = {};
  for (const key of searchParams.keys()) {
    references[key] = searchParams.get(key);
  }

  const {
    data: auditHistory,
    isLoading,
    isError,
  } = useGetMortgageHistoryAuditData(pageEnum, entityEnum, references);

  return (
    <Layout
      pageEnum={pageEnum}
      isLoading={isLoading}
      isError={isError}
      isDataEmpty={!auditHistory?.length}
    >
      {auditHistory
        ?.filter((entity) => entity.entityType !== LOAN_ENTITY_TYPE)
        .map((entity) => (
          <AccordionContainer
            key={entity.title ?? `liability-${entity.entityType}`}
            header={
              entity.title ? entity.title : `liability-${entity.entityType}`
            }
          >
            {renderLiabilitySection(entity)}
          </AccordionContainer>
        ))}
      {auditHistory
        ?.filter((entity) => entity.entityType === LOAN_ENTITY_TYPE)
        .map((entity) => (
          <div key="loan-questions">{renderLiabilitySection(entity)}</div>
        ))}
    </Layout>
  );
}
